import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Tab, Tabs } from "react-bootstrap";
import api from "../../api";
import ProjectWorkspaceHeader from "./ProjectWorkspaceHeader";
import ProjectWorkspaceOverviewTab from "./ProjectWorkspaceOverviewTab";
import ProjectWorkspaceProcessTab from "./ProjectWorkspaceProcessTab";
import ProjectWorkspaceBoardTab from "./ProjectWorkspaceBoardTab";
import ProjectWorkspaceTimelineTab from "./ProjectWorkspaceTimelineTab";
import ProjectWorkspaceMilestonesTab from "./ProjectWorkspaceMilestonesTab";
import ProjectWorkspaceActivityTab from "./ProjectWorkspaceActivityTab";
import ProjectWorkspaceDailyWorkTab from "./ProjectWorkspaceDailyWorkTab";
import ProjectWorkspaceUptimeTab from "./ProjectWorkspaceUptimeTab";
import {
  FinalPlanMilestones,
  FinalPlanOverview,
  FinalPlanProcess,
  FinalPlanTimeline,
  PlanViewSwitch,
} from "./ProjectWorkspaceFinalPlan";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import { usePermissions } from "../../context/PermissionContext";
import "./ProjectWorkspace.css";

// src/pages/admins/ProjectWorkspacePage.jsx
//
// Full-page Project Workspace shell, mounted at /admin/projects/:projectId
// (see routes/AppRoutes.js / ProjectDetailPage.jsx). Fetches
// GET /projects/:id/overview exactly ONCE here and shares the result with
// ProjectWorkspaceHeader and the Overview tab via the `overview`/`loading`/
// `error`/`onRefresh` props those two components already expect -- neither
// of them fetches it a second time. The other five tabs (Process/Board/
// Timeline/Milestones/Activity) are entirely self-contained/self-fetching,
// so this shell knows nothing about their internals; it only lazy-mounts
// them (mountOnEnter/unmountOnExit) so a tab's own fetch only fires once
// the user actually selects it.
const WORKSPACE_TABS = new Set([
  "overview",
  "process",
  "board",
  "timeline",
  "milestones",
  "daily-work",
  "activity",
  "uptime",
]);

function initialWorkspaceTab() {
  if (typeof window === "undefined") return "overview";
  const requested = new URLSearchParams(window.location.search).get("tab");
  return WORKSPACE_TABS.has(requested) ? requested : "overview";
}

function initialPlanView() {
  if (typeof window === "undefined") return "final";
  return new URLSearchParams(window.location.search).get("plan") === "historical" ? "historical" : "final";
}

// Phase 90: Overview/Process/Timeline/Milestones show the final plan
// (M1-M8 -> Module -> Screen) when the project has one, with the historical
// D01-D11 phase views one click away. A project without a final plan sees
// exactly the historical views, with no switch.
function PlanAwareTab({ finalPlan, view, onViewChange, final, historical }) {
  const { data, loading, error, reload } = finalPlan;
  if (loading && !data) return <LoadingState message="Loading final plan…" />;
  if (error && !data) {
    return (
      <>
        <ErrorState
          title="Final plan could not be loaded"
          message={error}
          action={<button type="button" className="unit-btn unit-btn-light" onClick={reload}>Retry</button>}
        />
        {historical}
      </>
    );
  }
  if (!data?.available) return historical;
  return (
    <>
      <PlanViewSwitch view={view} onChange={onViewChange} plan={data} />
      {view === "final" ? final(data) : historical}
    </>
  );
}

export default function ProjectWorkspacePage() {
  const { projectId } = useParams();
  const [overview, setOverview] = useState(null);
  // UI-only: hide the Uptime tab from roles that cannot read monitors, so
  // they don't land on a 403. The backend still enforces access itself.
  const { canAny, loading: permissionsLoading } = usePermissions();
  const canViewUptime = !permissionsLoading && canAny([
    { moduleKey: "uptime_monitoring", actionKey: "view_all" },
    { moduleKey: "uptime_monitoring", actionKey: "view_project" },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(initialWorkspaceTab);

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get(`/projects/${projectId}/overview`)
      .then((res) => setOverview(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.message || "Failed to load project"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // One fetch of GET /projects/:id/final-plan shared by the four plan-aware
  // tabs, so their counts always come from the same response.
  const [planView, setPlanView] = useState(initialPlanView);
  const [plan, setPlan] = useState({ data: null, loading: true, error: "" });
  const loadPlan = () => {
    setPlan((prev) => ({ ...prev, loading: true, error: "" }));
    api
      .get(`/projects/${projectId}/final-plan`)
      .then((res) => setPlan({ data: res.data?.data || null, loading: false, error: "" }))
      .catch((err) => setPlan((prev) => ({ ...prev, loading: false, error: err.response?.data?.message || "Failed to load final plan" })));
  };
  useEffect(loadPlan, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const finalPlan = { ...plan, reload: loadPlan };
  const planTab = (final, historical) => (
    <PlanAwareTab finalPlan={finalPlan} view={planView} onViewChange={setPlanView} final={final} historical={historical} />
  );

  return (
    <div className="pw-page">
      <ProjectWorkspaceHeader
        projectId={projectId}
        overview={overview}
        loading={loading}
        error={error}
        onRefresh={() => { load(); loadPlan(); }}
        finalPlan={planView === "final" ? plan.data : null}
      />

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "overview")}
        className="pw-tabs"
        mountOnEnter
        unmountOnExit
      >
        <Tab eventKey="overview" title="Overview">
          {planTab(
            (data) => <FinalPlanOverview plan={data} overview={overview} />,
            <ProjectWorkspaceOverviewTab projectId={projectId} overview={overview} />
          )}
        </Tab>
        <Tab eventKey="process" title="Process">
          {planTab(
            (data) => <FinalPlanProcess plan={data} />,
            <ProjectWorkspaceProcessTab projectId={projectId} />
          )}
        </Tab>
        <Tab eventKey="board" title="Board">
          <ProjectWorkspaceBoardTab projectId={projectId} />
        </Tab>
        <Tab eventKey="timeline" title="Timeline">
          {planTab(
            (data) => <FinalPlanTimeline plan={data} />,
            <ProjectWorkspaceTimelineTab projectId={projectId} />
          )}
        </Tab>
        <Tab eventKey="milestones" title="Milestones">
          {planTab(
            (data) => <FinalPlanMilestones plan={data} projectId={projectId} onChanged={() => { loadPlan(); load(); }} />,
            <ProjectWorkspaceMilestonesTab projectId={projectId} />
          )}
        </Tab>
        <Tab eventKey="daily-work" title="Daily Work">
          <ProjectWorkspaceDailyWorkTab projectId={projectId} />
        </Tab>
        <Tab eventKey="activity" title="Activity">
          <ProjectWorkspaceActivityTab projectId={projectId} />
        </Tab>
        {/* Uptime Kuma integration, Phase 1: server-scoped to THIS project's
            mapped monitors only (see ProjectWorkspaceUptimeTab's own header
            comment). The tab is hidden for roles without an uptime_monitoring
            view permission; the backend remains the only authority. */}
        {canViewUptime && (
          <Tab eventKey="uptime" title="Uptime">
            <ProjectWorkspaceUptimeTab projectId={projectId} />
          </Tab>
        )}
      </Tabs>
    </div>
  );
}
